import React, { useEffect, useState } from "react";
import logo from "../assets/logo.png";
import { HiMenu, HiX } from "react-icons/hi";
import { FiPhone } from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

export default function NavBar() {
  const { t, language, setLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { title: t("nav.tariffs"), href: "#tariffs" },
    { title: t("nav.speedtest"), href: "#speedtest" },
    { title: t("nav.coverage"), href: "#coverage" },
    { title: t("nav.about"), href: "#about" },
    { title: t("nav.contacts"), href: "#contacts" },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const targetId = href.replace("#", "");
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  const languages = [
    { code: "am", label: "ՀԱՅ" },
    { code: "ru", label: "РУС" },
    { code: "en", label: "ENG" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-40 transition-all duration-300 ${scrolled
          ? "bg-white/95 backdrop-blur-md shadow-md py-2.5 border-b border-slate-100"
          : "bg-white/90 backdrop-blur-sm py-4 border-b border-slate-100/60"
        }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between">
        {/* Brand Logo */}
        <a
          href="#realtime"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center group py-0.5"
        >
          <img
            src={logo}
            alt="Real Time Telecom"
            className="w-[150px] md:w-[180px] h-auto object-contain transition-transform group-hover:scale-[1.02]"
          />
        </a>

        {/* Desktop Navigation Links */}
        <div className="hidden lg:flex items-center space-x-7">
          <ul className="flex items-center space-x-6 list-none m-0 p-0">
            {navLinks.map((nav, i) => (
              <li key={i}>
                <a
                  href={nav.href}
                  onClick={(e) => handleNavClick(e, nav.href)}
                  className="text-slate-600 hover:text-brandRed text-xs font-bold tracking-wider uppercase transition-colors duration-200"
                >
                  {nav.title}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Desktop Right Actions: Language Switcher & Hotline */}
        <div className="hidden lg:flex items-center space-x-3.5">
          {/* Language Switcher Pill */}
          <div className="flex items-center bg-slate-100/90 p-1 rounded-full border border-slate-200/80">

            {languages.map((item) => (
              <button
                key={item.code}
                onClick={() => setLanguage(item.code)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold tracking-wider transition-all cursor-pointer ${language === item.code
                    ? "bg-brandRed text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                  }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Hotline Call Button */}
          <a
            href="tel:+37460666999"
            className="flex items-center space-x-2 bg-red-50 hover:bg-brandRed text-brandRed hover:text-white px-5 py-2.5 rounded-full border border-red-100 hover:border-brandRed transition-all duration-200 shadow-sm"
          >
            <FiPhone size={14} />
            <span className="font-bebas text-base tracking-widest leading-none pt-0.5">
              060 666 999
            </span>
          </a>
        </div>

        {/* Mobile Menu Button & Language Indicator */}
        <div className="lg:hidden flex items-center space-x-2">
          {/* Mobile Quick Lang Switch */}
          <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-bold">
            {languages.map((item) => (
              <button
                key={item.code}
                onClick={() => setLanguage(item.code)}
                className={`px-2 py-0.5 rounded-md ${language === item.code
                    ? "bg-brandRed text-white"
                    : "text-slate-600"
                  }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <button
            className="text-slate-700 hover:text-brandRed p-1.5 rounded-xl hover:bg-slate-50 transition-colors focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Menu"
          >
            {mobileMenuOpen ? <HiX size={26} /> : <HiMenu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden w-full bg-white border-b border-slate-100 shadow-xl py-5 px-6 animate-fadeIn">
          <ul className="flex flex-col space-y-3 list-none m-0 p-0 border-b border-slate-100 pb-4">
            {navLinks.map((nav, i) => (
              <li key={i}>
                <a
                  href={nav.href}
                  onClick={(e) => handleNavClick(e, nav.href)}
                  className="block text-slate-700 hover:text-brandRed text-sm font-bold uppercase tracking-wider py-1.5 transition-colors"
                >
                  {nav.title}
                </a>
              </li>
            ))}
          </ul>

          <div className="pt-4 space-y-3">
            <a
              href="tel:+37460666999"
              className="w-full flex items-center justify-center space-x-2.5 bg-brandRed text-white py-3 px-4 rounded-xl text-base font-black font-bebas tracking-widest shadow-glow-red"
            >
              <FiPhone size={16} />
              <span>{t("nav.callUs")}</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
